import { useState } from "react";
import { Button, Modal, message } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitPublishDiffDto } from "../../../../domain/DataKitDto";
import "./DataKitPublishBar.scss";

interface Props {
  kit: DataKitDto;
  diff: DataKitPublishDiffDto;
  canWrite: boolean;
  onDone: () => void;
}

export function DataKitPublishBar({ kit, diff, canWrite, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (diff.totalChanges === 0) return null;

  const count = diff.totalChanges;

  const publish = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.publishKit(kit.id)
      .then((res) => {
        const r = res.data;
        message.success(
          `Published ${r.itemsPublished} item${r.itemsPublished === 1 ? "" : "s"} to every design using this kit`
        );
        onDone();
      })
      .catch(() => message.error("Could not publish this kit"))
      .finally(() => setBusy(false));
  };

  const discard = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.discardKit(kit.id)
      .then(() => {
        setConfirmDiscard(false);
        onDone();
      })
      .catch(() => message.error("Could not discard these changes"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="data-kit-publish-bar">
      <WarningOutlined className="data-kit-publish-bar-icon" />
      <p className="data-kit-publish-bar-copy">
        <strong>
          {count} unpublished change{count === 1 ? "" : "s"}
        </strong>
        <span> — designs still show the last published values.</span>
      </p>
      {canWrite && (
        <>
          <Button
            type="primary"
            className="data-kit-publish-bar-btn"
            onClick={publish}
            loading={busy}
          >
            Publish to designs
          </Button>
          <button
            type="button"
            className="data-kit-publish-bar-discard"
            onClick={() => setConfirmDiscard(true)}
            disabled={busy}
          >
            Discard
          </button>
        </>
      )}

      <Modal
        open={confirmDiscard}
        title="Discard unpublished changes?"
        okText="Discard"
        okButtonProps={{ danger: true }}
        confirmLoading={busy}
        onOk={discard}
        onCancel={() => setConfirmDiscard(false)}
      >
        <p className="data-kit-discard-copy">
          Every edit since the last publish goes back to its published value, and items created
          since then are deleted. Item availability is not affected — it never waits for a publish.
        </p>
      </Modal>
    </div>
  );
}
